import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { ProjectSelectorTrigger } from "./ProjectSelectorTrigger";
import { ProjectSelectorModal } from "./ProjectSelectorModal";
import { useProjectSelector } from "../../hooks/useProjectSelector";
import {
  CreateProjectWizardModal,
  type CreateWizardInitialMode,
} from "../CreateProjectWizard";
import { configService } from "../../services/configService";
import { useOrganization } from "../../../providers/organization-provider";

interface ProjectSelectorProps {
  onSelectExecution?: (executionId: string, workpillId: string | null, executionName?: string) => void;
  onRenameProject?: (executionId: string, newName: string) => Promise<boolean>;
  selectedExecutionId?: string;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  onSelectExecution,
  onRenameProject: externalOnRenameProject,
  selectedExecutionId: externalSelectedExecutionId,
  isOpen: externalIsOpen,
  onOpenChange: externalOnOpenChange,
}) => {
  const { organizationId } = useOrganization();
  const {
    selectedProject,
    isModalOpen: internalIsModalOpen,
    openModal: internalOpenModal,
    closeModal: internalCloseModal,
    selectProject,
    refreshProjects,
    updateProjectName,
    filteredProjects,
    searchTerm,
    setSearchTerm,
    selectedWorkPill,
    setSelectedWorkPill,
    uniqueWorkPills,
    sortBy,
    setSortBy,
  } = useProjectSelector(externalSelectedExecutionId);

  const isControlled = externalIsOpen !== undefined;
  const isModalOpen = isControlled ? externalIsOpen : internalIsModalOpen;

  const handleOpenModal = () => {
    if (isControlled) {
      externalOnOpenChange?.(true);
    } else {
      internalOpenModal();
    }
  };

  const handleCloseModal = () => {
    if (isControlled) {
      externalOnOpenChange?.(false);
    } else {
      internalCloseModal();
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      refreshProjects();
    }
  }, [isModalOpen, refreshProjects]);

  const [createWizardOpen, setCreateWizardOpen] = useState(false);
  const [createWizardMode, setCreateWizardMode] = useState<CreateWizardInitialMode>("blank");
  const skipReopenProjectPicker = useRef(false);

  const openCreateWizard = (mode: CreateWizardInitialMode) => {
    handleCloseModal();
    setCreateWizardMode(mode);
    setCreateWizardOpen(true);
  };

  const handleSelectProject = (executionId: string) => {
    selectProject(executionId);

    const project = filteredProjects.find((p) => p.id === executionId);
    if (project && onSelectExecution) {
      onSelectExecution(project.id, project.workpill_id, project.name);
    }

    handleCloseModal();
  };

  const handleRenameProject = async (newName: string) => {
    if (!selectedProject) return false;

    if (externalOnRenameProject) {
      return await externalOnRenameProject(selectedProject.id, newName);
    }

    return await updateProjectName(selectedProject.id, newName);
  };

  return (
    <>
      <ProjectSelectorTrigger
        selectedProject={selectedProject}
        onClick={handleOpenModal}
        isOpen={isModalOpen}
        onRename={handleRenameProject}
      />

      <ProjectSelectorModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        projects={filteredProjects}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSelectProject={handleSelectProject}
        selectedExecutionId={selectedProject?.id}
        onCreateProject={() => openCreateWizard("workpill")}
        onCreateBlankProject={() => openCreateWizard("blank")}
        selectedWorkPill={selectedWorkPill}
        onWorkPillChange={setSelectedWorkPill}
        uniqueWorkPills={uniqueWorkPills}
        sortBy={sortBy}
        onSortByChange={setSortBy}
      />

      <CreateProjectWizardModal
        open={createWizardOpen}
        onOpenChange={(open) => {
          setCreateWizardOpen(open);
          if (!open) {
            if (!skipReopenProjectPicker.current) {
              handleOpenModal();
            }
            skipReopenProjectPicker.current = false;
          }
        }}
        initialMode={createWizardMode}
        organizationId={organizationId}
        userId={configService.getUserId()}
        onCreated={async (execution) => {
          skipReopenProjectPicker.current = true;
          await refreshProjects();
          selectProject(execution.id);
          if (onSelectExecution) {
            onSelectExecution(execution.id, execution.workpill_id, execution.name);
          }
        }}
      />
    </>
  );
};
